/**
 * Saved views (Phase 7, Lane O): a named set of filters for Find exercises or the Sheets
 * page, kept on the account (`/api/views`). Your own and everyone's shared views show as
 * chips above the results; one click applies a view, "Save this view" keeps the current
 * filters under a name. The command palette lists every view as "Open view: <name>".
 *
 * A view is the page's query string. Find exercises keeps all of its filters in the URL
 * already (libraryUrl.ts); the Sheets page's are `view`, `term`, `q` and `mine`.
 */
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Bookmark, BookmarkPlus, ChevronDown, Users } from 'lucide-react';
import type { SavedView, SavedViewScope } from '@studio/shared';
import { prefsApi } from '../../api';
import { useAuth } from '../../auth';
import { useCommands } from '../../commands/registry';
import { Menu } from '../../pages/sheet-editor/Menu';
import { useToast } from '../Toast';
import { Button, Modal } from '../ui';
import { localized } from '../../i18n';

export const T = localized('saved views', {
  en: {
    views: 'Views',
    save: 'Save this view',
    saveTitle: 'Save this view',
    name: 'Name',
    namePlaceholder: 'e.g. Recursion, with solution',
    share: 'Share with everyone',
    shareHint: 'Everyone signed in sees it in their views; only you (and admins) can change it.',
    saved: (name: string) => `Saved the view “${name}”`,
    sharedBy: (name: string | null) => (name ? `Shared by ${name}` : 'Shared'),
    yours: 'Your view',
    sharedChip: 'shared',
    rename: 'Rename…',
    renameTitle: 'Rename the view',
    shareOn: 'Share with everyone',
    shareOff: 'Stop sharing',
    updateFilters: 'Use the current filters',
    remove: 'Delete',
    confirmRemove: (name: string) => `Delete the view “${name}”?`,
    open: (name: string) => `Open view: ${name}`,
    group: 'Saved views',
    cancel: 'Cancel',
    failed: 'The view could not be saved',
    scopes: { library: 'Find exercises', sheets: 'Sheets' } as Record<SavedViewScope, string>,
  },
  de: {
    views: 'Ansichten',
    save: 'Diese Ansicht speichern',
    saveTitle: 'Diese Ansicht speichern',
    name: 'Name',
    namePlaceholder: 'z. B. Rekursion, mit Lösung',
    share: 'Mit allen teilen',
    shareHint: 'Alle Angemeldeten sehen sie in ihren Ansichten; nur du (und Admins) können sie ändern.',
    saved: (name: string) => `Ansicht „${name}“ gespeichert`,
    sharedBy: (name: string | null) => (name ? `Geteilt von ${name}` : 'Geteilt'),
    yours: 'Deine Ansicht',
    sharedChip: 'geteilt',
    rename: 'Umbenennen…',
    renameTitle: 'Ansicht umbenennen',
    shareOn: 'Mit allen teilen',
    shareOff: 'Nicht mehr teilen',
    updateFilters: 'Aktuelle Filter übernehmen',
    remove: 'Löschen',
    confirmRemove: (name: string) => `Ansicht „${name}“ löschen?`,
    open: (name: string) => `Ansicht öffnen: ${name}`,
    group: 'Gespeicherte Ansichten',
    cancel: 'Abbrechen',
    failed: 'Die Ansicht konnte nicht gespeichert werden',
    scopes: { library: 'Aufgaben finden', sheets: 'Blätter' },
  },
});

/* ------------------------------------------------------------------ store */

/*
 * One list for the whole app: the chips on two pages and the palette's entries read the
 * same views, and a save on one page shows on the other without a reload.
 */
let views: SavedView[] | null = null;
/** whose views `views` are */
let owner: number | null = null;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

export function reloadViews(): Promise<void> {
  loading = prefsApi
    .views()
    .then((list) => {
      views = list;
    })
    .catch(() => {
      // unreachable: the chips stay as they were, or absent
      views = views ?? [];
    })
    .finally(() => {
      loading = null;
      emit();
    });
  return loading;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Every view the signed-in user can see, loaded on first use. */
export function useSavedViews(scope?: SavedViewScope): SavedView[] | null {
  const { user } = useAuth();
  const all = useSyncExternalStore(subscribe, () => views);
  const userId = user?.id ?? null;
  useEffect(() => {
    if (userId === null) {
      owner = null;
      return;
    }
    if (owner !== userId) {
      // another person on this tab: their views, not the previous one's
      owner = userId;
      views = null;
      void reloadViews();
    } else if (views === null && !loading) void reloadViews();
  }, [userId]);
  if (!all) return null;
  return scope ? all.filter((v) => v.scope === scope) : all;
}

/** A query string in a stable order, so a view and the page's current filters compare. */
export function normalizeQuery(query: string): string {
  const params = new URLSearchParams(query.replace(/^\?/, ''));
  const pairs = [...params.entries()].filter(([, v]) => v !== '');
  pairs.sort(([a, x], [b, y]) => (a === b ? x.localeCompare(y) : a.localeCompare(b)));
  return new URLSearchParams(pairs).toString();
}

export const viewPath = (view: Pick<SavedView, 'scope' | 'query'>): string =>
  `${view.scope === 'library' ? '/exercises' : '/sheets'}${view.query ? `?${view.query}` : ''}`;

/* ---------------------------------------------------------------- commands */

/** "Open view: <name>" in the command palette, for every view; mounted once in the shell. */
export function SavedViewCommands() {
  const list = useSavedViews();
  const navigate = useNavigate();
  useCommands(
    (list ?? []).map((v) => ({
      id: `view.open.${v.id}`,
      title: T.open(v.name),
      group: T.group,
      keywords: `${T.scopes[v.scope]} saved filter ${v.shared ? 'shared' : ''}`,
      run: () => navigate(viewPath(v)),
    })),
  );
  return null;
}

/* -------------------------------------------------------------------- bar */

/**
 * The chips of one page's views, and "Save this view". `current` is the page's query
 * string now; `onApply` puts a view's query string into effect.
 */
export function SavedViewBar({
  scope,
  current,
  onApply,
  className,
}: {
  scope: SavedViewScope;
  current: string;
  onApply: (query: string) => void;
  className?: string;
}) {
  const list = useSavedViews(scope);
  const { push } = useToast();
  const [saving, setSaving] = useState(false);
  const [renaming, setRenaming] = useState<SavedView | null>(null);
  const here = normalizeQuery(current);
  // the power layer: the palette saves the page's filters too
  useCommands([
    { id: `views.save.${scope}`, title: T.save, group: T.group, keywords: 'bookmark filters saved view', run: () => setSaving(true) },
  ]);

  const change = useCallback(
    async (view: SavedView, changes: { name?: string; query?: string; shared?: boolean }) => {
      try {
        await prefsApi.updateView(view.id, changes);
        await reloadViews();
      } catch (err) {
        push(err instanceof Error ? err.message : T.failed, 'error');
      }
    },
    [push],
  );
  const remove = useCallback(
    async (view: SavedView) => {
      if (!confirm(T.confirmRemove(view.name))) return;
      try {
        await prefsApi.deleteView(view.id);
        await reloadViews();
      } catch (err) {
        push(err instanceof Error ? err.message : T.failed, 'error');
      }
    },
    [push],
  );

  return (
    <div className={clsx('flex min-w-0 flex-wrap items-center gap-1.5', className)} data-testid={`saved-views-${scope}`}>
      <span className="label flex items-center gap-1">
        <Bookmark size={12} aria-hidden /> {T.views}
      </span>
      {(list ?? []).map((v) => {
        const active = normalizeQuery(v.query) === here;
        return (
          <span
            key={v.id}
            className={clsx(
              'inline-flex max-w-full items-center rounded-full border text-xs',
              active
                ? 'border-[var(--color-brand-600)] bg-[color-mix(in_srgb,var(--color-brand-600)_10%,transparent)] text-[var(--color-brand-600)]'
                : 'border-[var(--line)] bg-[var(--surface)] text-[var(--ink-soft)]',
            )}
            data-view-id={v.id}
          >
            <button
              type="button"
              className="flex min-w-0 items-center gap-1 rounded-full py-0.5 pl-2.5 pr-2 hover:text-[var(--ink)]"
              aria-pressed={active}
              title={v.ownerId !== null && !v.mine ? T.sharedBy(v.ownerName) : v.shared ? `${T.yours} · ${T.sharedChip}` : T.yours}
              onClick={() => onApply(v.query)}
            >
              {v.shared && <Users size={11} aria-label={T.sharedChip} />}
              <span className="truncate">{v.name}</span>
            </button>
            {v.mine && (
              <Menu
                label={`${v.name}: ${T.group}`}
                trigger={<ChevronDown size={12} />}
                triggerClassName="mr-1 rounded-full p-0.5 text-[var(--ink-faint)] hover:bg-[var(--surface-muted)] hover:text-[var(--ink)]"
                align="left"
                entries={[
                  { id: 'rename', label: T.rename, onSelect: () => setRenaming(v) },
                  { id: 'share', label: v.shared ? T.shareOff : T.shareOn, onSelect: () => void change(v, { shared: !v.shared }) },
                  { id: 'update', label: T.updateFilters, disabled: active, onSelect: () => void change(v, { query: current }) },
                  { id: 'delete', label: T.remove, danger: true, onSelect: () => void remove(v) },
                ]}
              />
            )}
          </span>
        );
      })}
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-full border border-dashed border-[var(--line-strong)] px-2.5 py-0.5 text-xs text-[var(--ink-soft)] hover:border-[var(--color-brand-600)] hover:text-[var(--color-brand-600)]"
        onClick={() => setSaving(true)}
        data-testid="save-view"
      >
        <BookmarkPlus size={12} aria-hidden /> {T.save}
      </button>
      {saving && (
        <NameDialog
          title={T.saveTitle}
          withShare
          onClose={() => setSaving(false)}
          onSubmit={async (name, shared) => {
            try {
              await prefsApi.createView({ scope, name, query: current, shared });
              await reloadViews();
              push(T.saved(name), 'success');
              setSaving(false);
            } catch (err) {
              push(err instanceof Error ? err.message : T.failed, 'error');
            }
          }}
        />
      )}
      {renaming && (
        <NameDialog
          title={T.renameTitle}
          initial={renaming.name}
          onClose={() => setRenaming(null)}
          onSubmit={async (name) => {
            await change(renaming, { name });
            setRenaming(null);
          }}
        />
      )}
    </div>
  );
}

function NameDialog({
  title,
  initial = '',
  withShare = false,
  onClose,
  onSubmit,
}: {
  title: string;
  initial?: string;
  withShare?: boolean;
  onClose: () => void;
  onSubmit: (name: string, shared: boolean) => Promise<void>;
}) {
  const [name, setName] = useState(initial);
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await onSubmit(name.trim(), shared);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {T.cancel}
          </Button>
          <Button variant="primary" loading={busy} disabled={!name.trim()} onClick={() => void submit()}>
            {title === T.saveTitle ? T.save : T.rename.replace('…', '')}
          </Button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="block text-sm">
          {T.name}
          <input
            className="input mt-1 w-full"
            value={name}
            autoFocus
            maxLength={80}
            placeholder={T.namePlaceholder}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        {withShare && (
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={shared} onChange={(e) => setShared(e.target.checked)} />
            <span>
              {T.share}
              <span className="block text-xs text-[var(--ink-faint)]">{T.shareHint}</span>
            </span>
          </label>
        )}
      </form>
    </Modal>
  );
}
